// --- src/components/CopyableInfoBox.js ---
import React, { useState, useCallback } from 'react';
import { Copy } from 'lucide-react';

// Updated props to potentially include network, though we pass the whole object for simplicity
const CopyableInfoBox = ({ id, cryptoData }) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const { label, address, network, isEmail = false } = cryptoData; // Destructure from passed object

  // Obfuscation logic remains the same, checks isEmail flag now part of cryptoData
  const getObfuscatedHtml = useCallback(() => {
    if (!isEmail) return null;
    return address.split('').map(char => `&#${char.charCodeAt(0)};`).join('');
  }, [address, isEmail]);

  // Copy handler now uses address directly from destructured props
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(address).then(() => {
      setShowFeedback(true);
      const timer = setTimeout(() => {
        setShowFeedback(false);
      }, 1500);
      return () => clearTimeout(timer);
    }).catch(err => {
      console.error('Failed to copy:', err);
    });
  }, [address]);

  return (
    <div
      className={`copyable-info-box border border-gray-200 rounded-md p-3 transition-colors duration-150 cursor-pointer relative group ${showFeedback ? 'bg-green-100' : 'bg-gray-50 hover:bg-gray-100'}`}
      onClick={handleCopy}
      title={`Click to copy ${label}`} // Updated title
    >
      <div className="flex items-start">
         <Copy size={14} className="mr-2 text-gray-500 flex-shrink-0 mt-0.5" />
         <div className="flex-1 min-w-0">
            {/* Main Label */}
            <span className="font-semibold text-sm text-gray-700">{label}</span>
            {/* Network Info - Added below label */}
            {!isEmail && network && (
                <span className="block text-xs text-gray-500 mt-0.5">Network: {network}</span>
            )}
            {/* Address/Value */}
            {isEmail ? (
                <span dangerouslySetInnerHTML={{ __html: getObfuscatedHtml() }} className="block text-sm break-words text-gray-900 select-all mt-1"></span>
            ) : (
                <span className="block text-sm break-words text-gray-900 select-all mt-1">{address}</span>
            )}
         </div>
      </div>

      {/* Feedback - remains the same */}
      {showFeedback && (
        <p className="text-green-600 text-xs font-semibold absolute bottom-1 right-2 transition-opacity duration-150 pointer-events-none">
          Copied!
        </p>
      )}
      <span className="hidden group-hover:lg:inline-block absolute top-1 right-1 text-xs text-gray-400 pointer-events-none">Copy</span>
    </div>
  );
};

export default CopyableInfoBox;